import { supabase } from '../lib/supabase'
import { HiddenTransaction } from '../types/database'

export class HiddenTransactionsService {
  private static TABLE_NAME = 'hidden_transactions'

  /**
   * Get all hidden transaction identifiers for a specific model
   */
  static async getByModelId(modelId: number): Promise<HiddenTransaction[]> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('model_id', modelId)

      if (error) {
        console.error('Error fetching hidden transactions:', error)
        throw error
      }

      return data || []
    } catch (error) {
      console.error('Error in getByModelId:', error)
      return []
    }
  }

  /**
   * Get the set of hidden transaction identifiers for a model (convenience method)
   */
  static async getHiddenIdentifiers(modelId: number): Promise<Set<string>> {
    const hidden = await this.getByModelId(modelId)
    return new Set(hidden.map(h => h.transaction_identifier))
  }

  /**
   * Hide a transaction (org-wide)
   */
  static async hide(modelId: number, transactionIdentifier: string): Promise<HiddenTransaction> {
    try {
      const { data: userData } = await supabase.auth.getUser()

      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .insert({
          model_id: modelId,
          transaction_identifier: transactionIdentifier,
          hidden_by: userData?.user?.id || null
        })
        .select()
        .single()

      if (error) {
        // Handle duplicate - transaction already hidden
        if (error.code === '23505') {
          console.warn('Transaction already hidden:', transactionIdentifier)
          const existing = await this.getByModelId(modelId)
          const match = existing.find(h => h.transaction_identifier === transactionIdentifier)
          if (match) return match
        }
        throw new Error(`Failed to hide transaction: ${error.message}`)
      }

      return data
    } catch (error) {
      console.error('Error hiding transaction:', error)
      throw error
    }
  }

  /**
   * Unhide a transaction (org-wide)
   */
  static async unhide(modelId: number, transactionIdentifier: string): Promise<void> {
    try {
      const { error } = await supabase
        .from(this.TABLE_NAME)
        .delete()
        .eq('model_id', modelId)
        .eq('transaction_identifier', transactionIdentifier)

      if (error) {
        throw new Error(`Failed to unhide transaction: ${error.message}`)
      }
    } catch (error) {
      console.error('Error unhiding transaction:', error)
      throw error
    }
  }

  /**
   * Get a unique identifier for a sold aircraft entry.
   * Uses aircraftTransaction.transaction_id when available,
   * falls back to 'api-{trans_id}' for API-only transactions.
   */
  static getTransactionIdentifier(
    aircraftTransaction?: { transaction_id: string } | null,
    transaction?: { trans_id: number } | null
  ): string | null {
    if (aircraftTransaction?.transaction_id) {
      return aircraftTransaction.transaction_id
    }
    if (transaction?.trans_id) {
      return `api-${transaction.trans_id}`
    }
    return null
  }
}

